import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SectionTitle from '../components/SectionTitle';

const StoryPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    industry: '',
    story: '',
    wishList: '',
    shareConsent: false,
    contactConsent: false
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  
  // Industry options
  const industries = [
    { value: 'gastronomy', label: 'Gastronomy' },
    { value: 'retail', label: 'Retail' },
    { value: 'coaches', label: 'Coaches & Consultants' },
    { value: 'creatives', label: 'Creatives & Artists' },
    { value: 'other', label: 'Other Industry' }
  ];

  // Story examples for inspiration
  const storyExamples = [
    {
      title: "Restaurant verliert 30% an Lieferdienst",
      snippet: "Seit wir bei der großen Lieferplattform sind, geht ein Drittel unseres Umsatzes für Provisionen drauf. Trotzdem können wir nicht aussteigen, weil wir sonst nicht mehr gefunden werden."
    },
    {
      title: "Coach muss Termine auf fünf Plattformen pflegen",
      snippet: "Als Coach muss ich meine Verfügbarkeit auf fünf verschiedenen Plattformen aktuell halten, sonst verpasse ich Kunden. Eine eigene Lösung wäre viel einfacher."
    },
    {
      title: "Einzelhändler durch Algorithmus ausgebremst",
      snippet: "Unser Shop lief gut auf der Plattform, bis der Algorithmus geändert wurde. Über Nacht brachen die Verkäufe ein, ohne dass wir etwas dagegen tun konnten."
    }
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (!formData.story.trim()) {
      errors.story = 'Bitte teile deine Geschichte mit uns';
    }
    
    if (!formData.email.trim()) {
      errors.email = 'Bitte gib deine E-Mail-Adresse ein';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Bitte gib eine gültige E-Mail-Adresse ein';
    }
    
    if (!formData.contactConsent) {
      errors.contactConsent = 'Bitte erlaube uns, dich zu kontaktieren';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // In a real application, you would submit the form to a backend
    console.log('Story submitted:', formData);
    
    // Show success message
    setFormSubmitted(true);
  };

  return (
    <main className="pt-24 pb-16">
      {/* Header Section */}
      <section className="py-16 bg-gradient-to-b from-ocean-50 to-white">
        <div className="container mx-auto px-4 md:px-6">
          <SectionTitle 
            title="Share Your Story"
            subtitle="Help us understand the challenges of platform dependency and shape future solutions."
            centered
          />
        </div>
      </section>

      {/* Introduction Text */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Digital platforms like Lieferando, Booking.com, Amazon, or Etsy offer visibility but often come with high fees, strict rules, and a lack of control. Small businesses, freelancers, and creatives increasingly find themselves dependent on these giants.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              We want to change that. By sharing your experiences, you help us develop solutions that promote digital independence. Your story can also inspire others to seek alternatives.
            </p>
          </div>
        </div>
      </section>

      {/* Story Examples */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4 md:px-6">
          <h2 className="text-2xl font-lexend font-bold text-center text-gray-800 mb-10">
            What kind of stories are we looking for?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {storyExamples.map((example, index) => (
              <div key={index} className="text-center">
                <p className="text-gray-600 mb-2">
                  {example.title}
                </p>
                <p className="text-gray-600 text-sm italic">
                  "{example.snippet}"
                </p>
              </div>
            ))}
            <div className="text-center">
              <p className="text-gray-600 mb-2">
                Not sure where to start? 
              </p>
              <Link to="/beispiele" className="text-ocean-600 hover:text-ocean-700 font-medium">
                View more examples
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Submission Form */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-2xl mx-auto bg-white p-8 md:p-10 rounded-lg shadow-xl border border-gray-100">
            <h2 className="text-3xl font-lexend font-bold text-gray-900 mb-8 text-center">
              Tell Us Your Story
            </h2>
            
            {formSubmitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-lexend font-semibold text-gray-900 mb-3">
                  Thank You!
                </h3>
                <p className="text-gray-600 mb-6">
                  Your story has been submitted successfully. We appreciate you taking the time to share your experience.
                </p>
                <Link 
                  to="/ideen" 
                  className="text-ocean-600 hover:text-ocean-700 font-medium">
                  See submitted ideas or suggest a new one
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-lexend font-medium text-gray-700 mb-1">
                    Your Name (or Pseudonym)
                  </label>
                  <input 
                    type="text" 
                    name="name" 
                    id="name" 
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 ${formErrors.name ? 'border-red-500 focus:ring-red-500/50' : 'border-gray-300 focus:border-ocean-500 focus:ring-ocean-500/50'}`}
                  />
                  {formErrors.name && <p className="text-xs text-red-600 mt-1">{formErrors.name}</p>}
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-lexend font-medium text-gray-700 mb-1">
                    Email Address (kept private)
                  </label>
                  <input 
                    type="email" 
                    name="email" 
                    id="email" 
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 ${formErrors.email ? 'border-red-500 focus:ring-red-500/50' : 'border-gray-300 focus:border-ocean-500 focus:ring-ocean-500/50'}`}
                  />
                  {formErrors.email && <p className="text-xs text-red-600 mt-1">{formErrors.email}</p>}
                </div>

                <div>
                  <label htmlFor="industry" className="block text-sm font-lexend font-medium text-gray-700 mb-1">
                    Your Industry / Sector
                  </label>
                  <select 
                    name="industry" 
                    id="industry" 
                    value={formData.industry}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 bg-white ${formErrors.industry ? 'border-red-500 focus:ring-red-500/50' : 'border-gray-300 focus:border-ocean-500 focus:ring-ocean-500/50'}`}
                  >
                    <option value="">Select your industry...</option>
                    {industries.map(industry => (
                      <option key={industry.value} value={industry.value}>{industry.label}</option>
                    ))}
                  </select>
                  {formErrors.industry && <p className="text-xs text-red-600 mt-1">{formErrors.industry}</p>}
                </div>

                <div>
                  <label htmlFor="story" className="block text-sm font-lexend font-medium text-gray-700 mb-1">
                    Your Story / Problem with Platforms
                  </label>
                  <textarea 
                    name="story" 
                    id="story" 
                    rows={6}
                    value={formData.story}
                    onChange={handleChange}
                    placeholder="Describe your experience... e.g., high fees, lack of control, unfair competition, algorithm changes, etc."
                    className={`w-full px-4 py-2.5 border rounded-md focus:ring-2 ${formErrors.story ? 'border-red-500 focus:ring-red-500/50' : 'border-gray-300 focus:border-ocean-500 focus:ring-ocean-500/50'}`}
                  />
                  {formErrors.story && <p className="text-xs text-red-600 mt-1">{formErrors.story}</p>}
                </div>
                
                <div>
                  <label htmlFor="wishList" className="block text-sm font-lexend font-medium text-gray-700 mb-1">
                    What solution would help you? (Optional)
                  </label>
                  <textarea 
                    name="wishList" 
                    id="wishList" 
                    rows={3}
                    value={formData.wishList}
                    onChange={handleChange}
                    placeholder="e.g., a commission-free booking tool, an independent online shop, a fair marketplace..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-md focus:ring-2 focus:border-ocean-500 focus:ring-ocean-500/50"
                  />
                </div>

                <div className="space-y-4 pt-2">
                  <div className="flex items-start">
                    <input 
                      id="shareConsent" 
                      name="shareConsent" 
                      type="checkbox" 
                      checked={formData.shareConsent}
                      onChange={handleChange}
                      className="h-4 w-4 text-ocean-600 border-gray-300 rounded focus:ring-ocean-500 mt-0.5"
                    />
                    <label htmlFor="shareConsent" className="ml-3 block text-sm text-gray-700">
                      I agree that my anonymized story may be published to illustrate the problem.
                    </label>
                  </div>
                  {formErrors.shareConsent && <p className="text-xs text-red-600">{formErrors.shareConsent}</p>}

                  <div className="flex items-start">
                    <input 
                      id="contactConsent" 
                      name="contactConsent" 
                      type="checkbox" 
                      checked={formData.contactConsent}
                      onChange={handleChange}
                      className="h-4 w-4 text-ocean-600 border-gray-300 rounded focus:ring-ocean-500 mt-0.5"
                    />
                    <label htmlFor="contactConsent" className="ml-3 block text-sm text-gray-700">
                      I agree to be contacted by Fork:it for further questions regarding my story.
                    </label>
                  </div>
                </div>

                <div className="pt-4">
                  <button 
                    type="submit" 
                    className="w-full bg-ocean-600 hover:bg-ocean-700 text-white font-lexend font-medium py-3 px-6 rounded-md shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ocean-500"
                  >
                    Submit My Story
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default StoryPage; 